
import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Check, RefreshCw, Upload, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Product {
  name: string;
  category: string | null;
  description: string | null;
  unit_of_measure?: string;
}

interface ProductPreviewProps {
  products: Product[];
  isPending: boolean;
  onUpload: () => void;
  onReset: () => void;
  parseErrors?: string[];
}

export const ProductPreview: React.FC<ProductPreviewProps> = ({
  products,
  isPending,
  onUpload,
  onReset,
  parseErrors = []
}) => {
  if (products.length === 0 && parseErrors.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {parseErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error parsing Excel file</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside mt-2">
              {parseErrors.map((error, index) => (
                <li key={index} className="text-sm">{error}</li>
              ))}
            </ul>
            <p className="mt-2 text-sm">Please check your file format and try again. Make sure:</p>
            <ul className="list-disc list-inside mt-1 text-sm">
              <li>The file has a 'product_name' column</li>
              <li>The column headers are in the first row</li>
              <li>There is at least one valid product with a name</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {products.length > 0 && (
        <>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Product Preview</h3>
            <div className="space-x-2">
              <Button 
                variant="outline" 
                onClick={onReset}
                disabled={isPending}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button 
                onClick={onUpload} 
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Products
                  </>
                )}
              </Button>
            </div>
          </div>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow key={`${product.name}-${index}`}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.unit_of_measure || 'kg'}</TableCell>
                    <TableCell>{product.category || '-'}</TableCell>
                    <TableCell className="truncate max-w-md">
                      {product.description || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="flex justify-end mt-4">
            <Button 
              onClick={onUpload} 
              disabled={isPending}
              size="lg"
            >
              {isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {products.length} Products
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
